import type { EventStatus } from '@/config/objects/events';
import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, Pause, Play, Send, Square } from 'lucide-react';

/**
 * Event lifecycle — single source of truth (config + mutation).
 *
 * The action configs, the per-status allow-list, and the mutation that POSTs a
 * transition all live here so the detail-page header and the events-list rows
 * stay in lockstep. The allow-list mirrors the backend's `STATUS_TRANSITIONS`
 * table in `apps/hunts/api/src/api/v1/events/events.model.ts`; a drift-guard
 * test in `EventDetailPage.test.tsx` asserts every UI action maps back to a
 * backend-allowed transition. The button group that consumes this lives in
 * `EventLifecycleButtons.tsx` (kept separate for React Fast Refresh).
 */
export type LifecycleAction = 'publish' | 'start' | 'pause' | 'resume' | 'end' | 'archive';

export interface LifecycleActionConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** POST endpoint segment: `/events/:id/{endpoint}`. */
  endpoint: LifecycleAction;
  confirmTitle: string;
  confirmDescription: string;
  variant?: 'default' | 'destructive' | 'outline';
  /** Past-tense verb for the success toast ("Event {pastTense} successfully"). */
  pastTense: string;
}

export const LIFECYCLE_ACTIONS: Record<LifecycleAction, LifecycleActionConfig> = {
  publish: {
    label: 'Publish',
    icon: Send,
    endpoint: 'publish',
    confirmTitle: 'Publish Event',
    confirmDescription:
      'This will make the event scheduled and visible to participants. Are you sure?',
    pastTense: 'published',
  },
  start: {
    label: 'Start Event',
    icon: Play,
    endpoint: 'start',
    confirmTitle: 'Start Event',
    confirmDescription: 'This will start the event and allow teams to participate. Are you sure?',
    pastTense: 'started',
  },
  pause: {
    label: 'Pause',
    icon: Pause,
    endpoint: 'pause',
    confirmTitle: 'Pause Event',
    confirmDescription:
      'This will pause the event. Teams will not be able to submit answers. Are you sure?',
    variant: 'outline',
    pastTense: 'paused',
  },
  resume: {
    label: 'Resume',
    icon: Play,
    endpoint: 'resume',
    confirmTitle: 'Resume Event',
    confirmDescription:
      'This will resume the event and allow teams to continue participating. Are you sure?',
    pastTense: 'resumed',
  },
  end: {
    label: 'End Event',
    icon: Square,
    endpoint: 'end',
    confirmTitle: 'End Event',
    confirmDescription:
      'This will end the event. Teams will no longer be able to participate. Are you sure?',
    variant: 'destructive',
    pastTense: 'ended',
  },
  archive: {
    label: 'Archive',
    icon: Archive,
    endpoint: 'archive',
    confirmTitle: 'Archive Event',
    confirmDescription: 'This will archive the event. Are you sure?',
    variant: 'outline',
    pastTense: 'archived',
  },
};

/**
 * Actions available per status, in priority order (the first entry is the
 * "primary" forward action surfaced on list rows).
 */
export const STATUS_ACTIONS: Record<EventStatus, readonly LifecycleAction[]> = {
  draft: ['publish', 'archive'],
  scheduled: ['start', 'archive'],
  active: ['pause', 'end'],
  paused: ['resume', 'end'],
  completed: ['archive'],
  archived: [],
};

/**
 * Mutation that POSTs a lifecycle transition and refreshes both the event
 * detail query and the events list (prefix-invalidating the `Event` key).
 *
 * @param eventId - Target event id.
 * @returns A react-query mutation; call `.mutate(action)`.
 */
export function useEventLifecycleMutation(eventId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (action: LifecycleAction) =>
      apiClient.post(`/events/${eventId}/${LIFECYCLE_ACTIONS[action].endpoint}`),
    onSuccess: (_response, action) => {
      queryClient.invalidateQueries({ queryKey: ['Event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['Event'] });
      toast.success(`Event ${LIFECYCLE_ACTIONS[action].pastTense} successfully`);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update event status');
    },
  });
}

/**
 * Whether the event's structure (groups, ordering, unlocks, adding/removing
 * challenges, artifacts, stacks) can still be edited. Mirrors the backend's
 * `requireStructureEditable` guard: allowed while drafting or scheduled,
 * locked once the event goes live.
 */
export function isStructureEditable(status?: EventStatus): boolean {
  return status === 'draft' || status === 'scheduled';
}
